import { Fragment } from 'react';

import { useCardPhrases } from './CardLanguage';
import {
  asArray,
  formatCardDate,
  formatCardTime,
  formatOrderHeading,
  safeHttpUrl,
  timelineStepLabel,
  visibleEvents,
} from './order-card-format';

import './ai-chat-cards.css';

export type CardActionPayload = { label: string; value: string; actionId?: string };
export type OnCardAction = (action: CardActionPayload) => void;

export type CardTimelineData = { type?: string; steps?: string[]; current?: number };

export type CardItem = {
  name: string;
  brand?: string;
  quantity?: number;
  size?: string;
  imageUrl?: string;
};

export type CardEvent = { timestamp?: string; description?: string; location?: string };

export function CardTimeline({ timeline }: { timeline?: CardTimelineData }) {
  const phrases = useCardPhrases();
  const steps = Array.isArray(timeline?.steps) ? timeline.steps : [];
  // Number.isFinite, aldrig truthiness: current === 0 är föraviseringens
  // normalläge och en falsy-koll släcker hela tidslinjen.
  const current = Number(timeline?.current);
  if (steps.length === 0 || !Number.isFinite(current)) return null;
  return (
    <ol className="ai-chat__timeline">
      {steps.map((step, index) => (
        <li
          key={`${step}-${index}`}
          className={index < current ? 'is-done' : index === current ? 'is-current' : undefined}
          aria-current={index === current ? 'step' : undefined}
        >
          {timelineStepLabel(step, phrases)}
        </li>
      ))}
    </ol>
  );
}

// Ingen död länk: URL → länk, värde → chatt-tur, annars dämpad text.
export function CardAction({
  label,
  url,
  value,
  actionId,
  quietLabel,
  onAction,
}: {
  label?: string;
  url?: string | null;
  value?: string;
  actionId?: string;
  quietLabel?: string;
  onAction?: OnCardAction;
}) {
  const phrases = useCardPhrases();
  const text = String(label ?? '').trim();
  const href = safeHttpUrl(url);
  if (href && text) {
    return (
      <a className="ai-chat__card-action" href={href} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );
  }
  const payload = String(value ?? '').trim();
  if (payload && text) {
    return (
      <button
        type="button"
        className="ai-chat__card-action"
        onClick={() => onAction?.({ label: text, value: payload, actionId })}
      >
        {text}
      </button>
    );
  }
  return <span className="ai-chat__card-quiet">{quietLabel || phrases.noUpdate}</span>;
}

export function ClickableOrderNumber({
  orderNumber,
  className,
  onAction,
}: {
  orderNumber: string;
  className: string;
  onAction?: OnCardAction;
}) {
  const phrases = useCardPhrases();
  const heading = formatOrderHeading(orderNumber, phrases);
  return (
    <button
      type="button"
      className={`${className} ai-chat__card-link`}
      onClick={() =>
        onAction?.({
          label: heading,
          value: `orderstatus order ${orderNumber}`,
          actionId: 'order.picker.status',
        })
      }
    >
      {heading}
    </button>
  );
}

export function OrderItemRows({ items }: { items?: CardItem[] }) {
  return (
    <>
      {asArray<CardItem | null>(items)
        .filter((item): item is CardItem => Boolean(item?.name))
        .map((item, index) => {
          const imageUrl = safeHttpUrl(item.imageUrl);
          const quantity = Number(item.quantity);
          const quantitySuffix = Number.isFinite(quantity) && quantity > 1 ? ` × ${quantity}` : '';
          return (
            <div className="ai-chat__order-item" key={`${item.name}-${index}`}>
              {imageUrl ? (
                <img className="ai-chat__order-item-thumb" src={imageUrl} alt="" loading="lazy" />
              ) : (
                <span className="ai-chat__order-item-thumb" aria-hidden="true" />
              )}
              <div className="ai-chat__order-item-text">
                {item.brand ? <div className="ai-chat__order-item-brand">{item.brand}</div> : null}
                <div className="ai-chat__order-item-name" title={item.name}>
                  {item.name}
                  {quantitySuffix}
                </div>
              </div>
              {item.size ? <div className="ai-chat__order-item-size">{item.size}</div> : null}
            </div>
          );
        })}
    </>
  );
}

export function CardFacts({ rows }: { rows: Array<[string, string]> }) {
  if (rows.length === 0) return null;
  return (
    <dl className="ai-chat__facts">
      {rows.map(([term, detail]) => (
        <Fragment key={term}>
          <dt>{term}</dt>
          <dd>{detail}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function EventRows({ events }: { events?: CardEvent[] }) {
  return (
    <>
      {visibleEvents<CardEvent>(events).map((event, index) => {
        const time = [formatCardDate(event?.timestamp), formatCardTime(event?.timestamp)]
          .filter(Boolean)
          .join(' ');
        const text = [event?.description?.trim(), event?.location?.trim()]
          .filter(Boolean)
          .join(' — ');
        if (!time && !text) return null;
        return (
          <div className="ai-chat__event" key={`${event?.timestamp ?? ''}-${index}`}>
            <div className="ai-chat__event-time">{time}</div>
            <div>{text}</div>
          </div>
        );
      })}
    </>
  );
}

export function CardEvents({ events }: { events?: CardEvent[] }) {
  const phrases = useCardPhrases();
  if (visibleEvents(events).length === 0) return null;
  return (
    <div className="ai-chat__card-events">
      <div className="ai-chat__card-items-label">{phrases.events}</div>
      <EventRows events={events} />
    </div>
  );
}
